/**
 * Выпадающие списки DevExtreme на телефоне открываются во весь экран (CAB-489).
 *
 * Список считает свой размер по ширине поля: 280×188 на мониторе — это шесть
 * читаемых строк, на телефоне те же 280px превращаются в окошко, куда надо
 * целиться. Пользователи кабинета — снабженцы и закупщики, часто не молодые,
 * и промах по строке в 32px стоит им лишнего действия каждый раз.
 *
 * Делаем это штатным механизмом виджета, а не только правилами CSS:
 * `_popupConfig()` редактора расширяется значениями `dropDownOptions`
 * (`ui/drop_down_editor/m_drop_down_editor.js`), а `fullScreen`, `showTitle` и
 * `showCloseButton` — обычные опции Popup. Так лист получает и honest-шапку с
 * крестиком: во весь экран без кнопки закрытия человек оказывался бы заперт —
 * нажать «мимо списка», как на мониторе, больше некуда.
 *
 * `device` — предикат, а не булево: он вычисляется при создании каждого
 * виджета, поэтому поворот планшета и смена размера окна учитываются без
 * перезагрузки (`core/options/utils.js`, `deviceFilter(currentDevice)`).
 */

import DateBox from "devextreme/ui/date_box";
import SelectBox from "devextreme/ui/select_box";
import i18n from "../../../../i18n";
import { MEDIA_MOBILE } from "../../constants/breakpoints";

/** Минимальный вид события Popup: нам нужен сам виджет. */
interface PopupShowingEvent {
  component?: {
    option(name: string): unknown;
    option(name: string, value: unknown): void;
  };
}

/**
 * Подпись листа выбора: берём ту, что человек уже видит рядом с полем.
 *
 * Порядок — от самого точного к самому общему: явная подпись для screen
 * reader, связанный `<label>`, подсказка внутри поля. Общее слово остаётся
 * запасным вариантом: пустая шапка с одним крестиком выглядит недоделанной.
 */
export function resolveDropdownTitle(anchor: Element | null | undefined, fallback: string): string {
  if (!anchor) return fallback;

  const aria = anchor.getAttribute("aria-label")?.trim();
  if (aria) return aria;

  const id = anchor.getAttribute("id");
  if (id) {
    const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;
    const label = document.querySelector(`label[for="${escaped}"]`)?.textContent?.trim();
    if (label) return label;
  }

  const placeholder = anchor.querySelector(".dx-placeholder")?.getAttribute("data-dx_placeholder")?.trim();
  if (placeholder) return placeholder;

  const inputPlaceholder = anchor.querySelector("input.dx-texteditor-input")?.getAttribute("placeholder")?.trim();
  if (inputPlaceholder) return inputPlaceholder;

  return fallback;
}

/** Элемент, к которому привязан лист: DevExtreme кладёт его в `position.of`. */
export function getDropdownAnchor(popupPosition: unknown): Element | null {
  if (!popupPosition || typeof popupPosition !== "object") return null;

  const of = (popupPosition as { of?: unknown }).of;
  if (of instanceof Element) return of;

  // jQuery-подобная обёртка DevExtreme
  const first = (of as { get?: (index: number) => unknown })?.get?.(0);
  return first instanceof Element ? first : null;
}

function isMobileViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;

  return window.matchMedia(MEDIA_MOBILE).matches;
}

export function applyDxMobileOverlays(): void {
  const device = () => isMobileViewport();

  const dropDownOptions = {
    fullScreen: true,
    showTitle: true,
    showCloseButton: true,
    onShowing(event: PopupShowingEvent): void {
      const popup = event.component;
      if (!popup) return;

      const anchor = getDropdownAnchor(popup.option("position"));
      popup.option("title", resolveDropdownTitle(anchor, i18n.global.t("main.selectValue")));
    },
  };

  SelectBox.defaultOptions({ device, options: { dropDownOptions } });
  DateBox.defaultOptions({ device, options: { dropDownOptions } });

}
